/**
 * Enhanced Error Logging Service
 * Provides comprehensive client-side error logging with multiple backends
 */

import { backendHealth } from '../utils/backendHealth';

class ErrorLoggingService {
  constructor(config = {}) {
    this.config = {
      // Service configuration
      enabled: config.enabled !== false,
      environment: config.environment || process.env.NODE_ENV || 'development',
      apiEndpoint: config.apiEndpoint || '/api/v1/errors',
      maxRetries: config.maxRetries || 3,
      retryDelay: config.retryDelay || 1000,

      // Sentry configuration (if available)
      sentryDsn: config.sentryDsn || process.env.REACT_APP_SENTRY_DSN,

      // Local storage configuration
      useLocalStorage: config.useLocalStorage !== false,
      maxLocalErrors: config.maxLocalErrors || 50,

      // Rate limiting
      maxErrorsPerMinute: config.maxErrorsPerMinute || 10,

      // User information
      userId: config.userId || null,
      sessionId: config.sessionId || this.generateSessionId(),

      // Additional context
      version: config.version || process.env.REACT_APP_VERSION || '1.0.0',
      buildId: config.buildId || process.env.REACT_APP_BUILD_ID || 'unknown',

      ...config,
    };

    this.errorQueue = [];
    this.errorCounts = new Map();
    this.isProcessing = false;

    // Initialize session storage
    this.initializeSession();

    // Set up global error handlers
    this.setupGlobalHandlers();

    // Initialize Sentry if available
    this.initializeSentry();

    // Start error processing
    this.startErrorProcessor();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  initializeSession() {
    try {
      const sessionData = {
        sessionId: this.config.sessionId,
        startTime: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      };

      if (this.config.useLocalStorage) {
        localStorage.setItem(
          'errorLogger_session',
          JSON.stringify(sessionData)
        );
      }
    } catch (error) {
      console.warn('Failed to initialize error logging session:', error);
    }
  }

  setupGlobalHandlers() {
    if (!this.config.enabled) return;

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
      this.logError({
        error: {
          name: 'UnhandledPromiseRejection',
          message: event.reason?.message || 'Unhandled promise rejection',
          stack: event.reason?.stack || 'No stack trace available',
        },
        context: {
          type: 'unhandledrejection',
          reason: event.reason,
        },
      });
    });

    // Handle global JavaScript errors
    window.addEventListener('error', event => {
      this.logError({
        error: {
          name: event.error?.name || 'JavaScriptError',
          message: event.message,
          stack: event.error?.stack || 'No stack trace available',
        },
        context: {
          type: 'javascript',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Handle resource loading errors
    window.addEventListener(
      'error',
      event => {
        if (event.target !== window) {
          this.logError({
            error: {
              name: 'ResourceLoadError',
              message: `Failed to load resource: ${event.target.src || event.target.href}`,
              stack: 'Resource loading error',
            },
            context: {
              type: 'resource',
              element: event.target.tagName,
              src: event.target.src || event.target.href,
            },
          });
        }
      },
      true
    );
  }

  initializeSentry() {
    if (this.config.sentryDsn && window.Sentry) {
      try {
        window.Sentry.init({
          dsn: this.config.sentryDsn,
          environment: this.config.environment,
          release: this.config.version,
          beforeSend: event => {
            // Add custom context
            event.user = {
              id: this.config.userId,
              session_id: this.config.sessionId,
            };
            event.tags = {
              ...event.tags,
              version: this.config.version,
              buildId: this.config.buildId,
            };
            return event;
          },
        });
      } catch (error) {
        console.warn('Failed to initialize Sentry:', error);
      }
    }
  }

  startErrorProcessor() {
    // Process error queue every 5 seconds. Skip the tick entirely when the
    // backend-health monitor has paused us — avoids POST-loop self-amplification
    // when /api/v1/errors itself returns 502.
    setInterval(() => {
      if (backendHealth.isErrorLoggerPaused()) return;
      this.processErrorQueue();
    }, 5000);

    // Clean up old error counts every minute
    setInterval(() => {
      this.cleanupErrorCounts();
    }, 60000);

    // Single-shot listener — log once per pause/resume cycle rather than on
    // every state change. The state-change microtask already coalesces bursts.
    backendHealth.subscribe(state => {
      if (state.pausedUntil) {
        console.warn(
          `[errorLogging] paused until ${new Date(state.pausedUntil).toISOString()} (consecutiveFailures=${state.consecutiveFailures})`
        );
      } else if (
        state.consecutiveFailures === 0 &&
        this.errorQueue.length > 0
      ) {
        // eslint-disable-next-line no-console
        console.log('[errorLogging] backend recovered, queue will resume');
      }
    });
  }

  async logError(errorData) {
    // Existing logError implementation remains unchanged
    if (!this.config.enabled) {
      console.warn('Error logging is disabled');
      return;
    }

    try {
      // Rate limiting check
      if (!this.isWithinRateLimit(errorData)) {
        console.warn('Error logging rate limit exceeded');
        return;
      }

      // Normalize error data
      const normalizedError = this.normalizeError(errorData);

      // Add to queue
      this.errorQueue.push(normalizedError);

      // Store in local storage as backup
      if (this.config.useLocalStorage) {
        this.storeErrorLocally(normalizedError);
      }

      // Log to Sentry if available
      if (window.Sentry) {
        this.logToSentry(normalizedError);
      }

      // Log to console in development
      if (this.config.environment === 'development') {
        console.error('Error logged:', normalizedError);
      }

      return normalizedError.errorId;
    } catch (error) {
      console.error('Failed to log error:', error);
    }
  }

  // New method for logging API errors
  async logAPIError(error, apiName = 'unknown', requestId = 'unknown') {
    const errorData = {
      error,
      context: {
        type: 'api_error',
        apiName,
        requestId,
      },
    };
    return this.logError(errorData);
  }

  normalizeError(errorData) {
    const timestamp = new Date().toISOString();
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      errorId,
      timestamp,
      sessionId: this.config.sessionId,
      userId: this.config.userId,
      environment: this.config.environment,
      version: this.config.version,
      buildId: this.config.buildId,

      // Error information
      error: {
        name: errorData.error?.name || 'UnknownError',
        message: errorData.error?.message || 'No error message',
        stack: errorData.error?.stack || 'No stack trace',
      },

      // Context information
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        ...errorData.context,
      },

      // Additional data
      errorInfo: errorData.errorInfo || null,
      userFeedback: errorData.userFeedback || null,
      severity: this.calculateSeverity(errorData.error),
      category: this.categorizeError(errorData.error),

      // Breadcrumbs (if available)
      breadcrumbs: this.getBreadcrumbs(),

      // Performance metrics
      performance: this.getPerformanceMetrics(),
    };
  }

  calculateSeverity(error) {
    if (!error) return 'medium';

    const errorMessage = error.message?.toLowerCase() || '';
    const errorName = error.name?.toLowerCase() || '';

    // Critical errors
    if (
      errorName.includes('syntaxerror') ||
      errorName.includes('referenceerror') ||
      errorMessage.includes('is not defined') ||
      errorMessage.includes('cannot read property')
    ) {
      return 'critical';
    }

    // High severity errors
    if (
      errorName.includes('typeerror') ||
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('timeout')
    ) {
      return 'high';
    }

    // Low severity errors
    if (
      errorName.includes('warning') ||
      errorMessage.includes('deprecated') ||
      errorMessage.includes('console')
    ) {
      return 'low';
    }

    return 'medium';
  }

  categorizeError(error) {
    if (!error) return 'unknown';

    const errorMessage = error.message?.toLowerCase() || '';
    const errorName = error.name?.toLowerCase() || '';

    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return 'network';
    }

    if (
      errorName.includes('syntaxerror') ||
      errorName.includes('referenceerror')
    ) {
      return 'javascript';
    }

    if (errorMessage.includes('react') || errorMessage.includes('component')) {
      return 'react';
    }

    if (errorMessage.includes('api') || errorMessage.includes('endpoint')) {
      return 'api';
    }

    return 'application';
  }

  isWithinRateLimit(errorData) {
    const now = Date.now();
    const errorKey = `${errorData.error?.name}_${errorData.error?.message}`;

    if (!this.errorCounts.has(errorKey)) {
      this.errorCounts.set(errorKey, []);
    }

    const timestamps = this.errorCounts.get(errorKey);

    // Remove timestamps older than 1 minute
    const oneMinuteAgo = now - 60000;
    const recentTimestamps = timestamps.filter(ts => ts > oneMinuteAgo);

    // Check if we're within the rate limit
    if (recentTimestamps.length >= this.config.maxErrorsPerMinute) {
      return false;
    }

    // Add current timestamp
    recentTimestamps.push(now);
    this.errorCounts.set(errorKey, recentTimestamps);

    return true;
  }

  cleanupErrorCounts() {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    for (const [key, timestamps] of this.errorCounts.entries()) {
      const recentTimestamps = timestamps.filter(ts => ts > oneMinuteAgo);
      if (recentTimestamps.length === 0) {
        this.errorCounts.delete(key);
      } else {
        this.errorCounts.set(key, recentTimestamps);
      }
    }
  }

  storeErrorLocally(errorData) {
    try {
      const stored = JSON.parse(
        localStorage.getItem('errorLogger_errors') || '[]'
      );
      stored.push(errorData);

      // Keep only the most recent errors
      if (stored.length > this.config.maxLocalErrors) {
        stored.splice(0, stored.length - this.config.maxLocalErrors);
      }

      localStorage.setItem('errorLogger_errors', JSON.stringify(stored));
    } catch (error) {
      console.warn('Failed to store error locally:', error);
    }
  }

  logToSentry(errorData) {
    try {
      if (window.Sentry) {
        window.Sentry.captureException(new Error(errorData.error.message), {
          tags: {
            errorId: errorData.errorId,
            category: errorData.category,
            severity: errorData.severity,
          },
          extra: {
            context: errorData.context,
            errorInfo: errorData.errorInfo,
            userFeedback: errorData.userFeedback,
          },
        });
      }
    } catch (error) {
      console.warn('Failed to log to Sentry:', error);
    }
  }

  async processErrorQueue() {
    if (this.isProcessing || this.errorQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      const errorsToSend = this.errorQueue.splice(0, 10); // Send up to 10 errors at once

      for (const errorData of errorsToSend) {
        await this.sendErrorToServer(errorData);
      }
    } catch (error) {
      console.error('Error processing queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async sendErrorToServer(errorData, retryCount = 0) {
    // Honor the shared health-monitor pause: skip the retry setTimeout entirely
    // when the monitor has flagged the backend down. The monitor itself decides
    // when to lift the pause based on a real success or 60s elapsed.
    if (backendHealth.isErrorLoggerPaused()) {
      return;
    }

    try {
      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData),
      });

      if (!response.ok) {
        // Record the failure on the shared monitor BEFORE scheduling the
        // retry so that subsequent retries see isDown / pausedUntil and bail.
        if (
          response.status === 502 ||
          response.status === 503 ||
          response.status === 504
        ) {
          backendHealth.recordFailure(`errorLogger POST ${response.status}`);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      backendHealth.recordSuccess();

      return await response.json();
    } catch (error) {
      // Network failures (fetch threw) also count toward the monitor
      if (error instanceof TypeError) {
        backendHealth.recordFailure(`errorLogger network`);
      }
      if (retryCount < this.config.maxRetries) {
        // Exponential backoff
        const delay = this.config.retryDelay * Math.pow(2, retryCount);
        setTimeout(() => {
          this.sendErrorToServer(errorData, retryCount + 1);
        }, delay);
      } else {
        console.error('Failed to send error to server after retries:', error);
      }
    }
  }

  getBreadcrumbs() {
    // Return navigation breadcrumbs if available
    try {
      const breadcrumbs = [];

      // Add current URL
      breadcrumbs.push({
        timestamp: new Date().toISOString(),
        message: `Navigated to ${window.location.href}`,
        category: 'navigation',
      });

      return breadcrumbs;
    } catch {
      return [];
    }
  }

  getPerformanceMetrics() {
    try {
      if (performance && performance.getEntriesByType) {
        const navigation = performance.getEntriesByType('navigation')[0];
        return {
          loadTime: navigation?.loadEventEnd - navigation?.loadEventStart,
          domContentLoaded:
            navigation?.domContentLoadedEventEnd -
            navigation?.domContentLoadedEventStart,
          memoryUsage: performance.memory
            ? {
                used: performance.memory.usedJSHeapSize,
                total: performance.memory.totalJSHeapSize,
                limit: performance.memory.jsHeapSizeLimit,
              }
            : null,
        };
      }
    } catch {
      return null;
    }
  }

  // Public methods for manual error reporting
  async sendErrorReport(reportData) {
    return this.logError({
      error: reportData.error,
      errorInfo: reportData.errorInfo,
      userFeedback: reportData.userFeedback,
      context: {
        type: 'user_report',
        ...reportData.context,
      },
    });
  }

  // Get stored errors from local storage
  getStoredErrors() {
    try {
      return JSON.parse(localStorage.getItem('errorLogger_errors') || '[]');
    } catch (error) {
      console.warn('Failed to get stored errors:', error);
      return [];
    }
  }

  // Clear stored errors
  clearStoredErrors() {
    try {
      localStorage.removeItem('errorLogger_errors');
    } catch (error) {
      console.warn('Failed to clear stored errors:', error);
    }
  }

  // Update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  // Get service status
  getStatus() {
    return {
      enabled: this.config.enabled,
      queueLength: this.errorQueue.length,
      isProcessing: this.isProcessing,
      errorCounts: Object.fromEntries(this.errorCounts),
      config: {
        ...this.config,
        sentryDsn: this.config.sentryDsn ? '[HIDDEN]' : null,
      },
    };
  }

  // Cleanup method for component unmounting
  destroy() {
    // Clear any pending timeouts or cleanup resources
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
    // Clear error queue
    this.errorQueue = [];
    // Remove global reference
    if (window.errorLogger === this) {
      delete window.errorLogger;
    }
  }
}

// Create global instance
const errorLogger = new ErrorLoggingService({
  enabled:
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_ERROR_LOGGING === 'true',
  environment: process.env.NODE_ENV,
  apiEndpoint: process.env.REACT_APP_ERROR_API_ENDPOINT || '/api/v1/errors',
  sentryDsn: process.env.REACT_APP_SENTRY_DSN,
  version: process.env.REACT_APP_VERSION,
  buildId: process.env.REACT_APP_BUILD_ID,
});

// Make it globally available
window.errorLogger = errorLogger;

export default errorLogger;
export { ErrorLoggingService };
